/**
 * `/agents/analytics/*` — Agents ▸ Analytics hub + You deep-dives (#44575).
 *
 * REWRITE of `excellent/app/agents/analytics/**` (778 LOC hub + 14 thin You
 * re-homes of @lens pages). Data access is `agentAnalytics.page` /
 * `agentAnalytics.you` (Hono); render is client-side.
 *
 * Grok / uncaptured cost groups render "—" via `cost_rows === 0`, never
 * `$0.00` — do not regress that.
 *
 * Live Next originals under `excellent/app/agents/analytics/**` are UNTOUCHED
 * (D6). Fleet redirect stubs in `agent-redirects.tsx` already target
 * `/agents/analytics#…` and light up once this hub is registered.
 */

import type { ReactNode } from "react";
import { useMemo } from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import {
  DataTable,
  ListCard,
  SectionTitleLink,
  StatStrip,
  PageHeader,
  SectionProductDashboard,
  makeSectionHrefRemapper,
  NavTabs,
  SectionNavRail,
  chartColor,
  formatUsd,
  formatCompact,
  roleLabel,
  useProvideRailExtension,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumn,
  type StatStripItem,
  type RailExtension,
  type NavTab,
  type SectionNavGroup,
  WorkerRunsTable,
  Button,
  SettingsPageShell,
  ProductIntegrationsPanel,
  type WorkerRunItem,
} from "@excellent/ui";
import {
  FleetSpendChart,
  type FleetSpendPoint,
} from "@excellent/ui/blocks/excellent/fleet-spend-chart";
import { FleetTrendChart } from "@excellent/ui/blocks/excellent/fleet-trend-chart";
import { MiniBarRow } from "@excellent/ui/blocks/lens/mini-bar-row";
import { useApi, useCursorList } from "@excellent/ui/api";
import { agentAnalytics } from "@excellent/ui/api/contracts/agent-analytics";
import { agentRuns } from "@excellent/ui/api/contracts/agent-runs";
import { RouterLink as Link, useNavigate } from "@excellent/ui/router";
import {
  Activity,
  ArrowLeft,
  Brain,
  CircleDollarSign,
  ClipboardList,
  Compass,
  Download,
  FolderKanban,
  History,
  ListTodo,
  MessageSquare,
  Settings as SettingsIcon,
  Wrench,
} from "@excellent/ui/icons";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { useParams } from "@tanstack/react-router";
import { lens } from "@excellent/ui/api/contracts/lens";
import ActivityPage from "../../src/capabilities/lens/app/activity/page";
import CostsPage from "../../src/capabilities/lens/app/costs/page";
import ExportPage from "../../src/capabilities/lens/app/export/page";
import HistoryPage from "../../src/capabilities/lens/app/history/page";
import MemoryPage from "../../src/capabilities/lens/app/memory/page";
import PlansPage from "../../src/capabilities/lens/app/plans/page";
import TodosPage from "../../src/capabilities/lens/app/todos/page";
import ToolsPage from "../../src/capabilities/lens/app/tools/page";
import SessionsPage from "../../src/capabilities/lens/app/sessions/page";
import SettingsPage from "../../src/capabilities/lens/app/settings/page";
import { ProjectsClient } from "../../src/capabilities/lens/app/projects/projects-client";
import SessionDetailPage from "../../src/capabilities/lens/app/sessions/[id]/page";

// ── shared chrome ───────────────────────────────────────────────────────────

const NUM = "tabular-nums text-foreground";
const MUTED = "tabular-nums text-muted-foreground";

const SECTION = {
  fleetSpend: "fleet-spend",
  fleetWorkflows: "fleet-workflows",
  fleetReliability: "fleet-reliability",
  fleetLanding: "fleet-landing",
  fleetRuns: "fleet-runs",
  youUsage: "you-usage",
} as const;

const ANALYTICS_OUTLINE = [
  { text: "Spend & ROI", anchor: SECTION.fleetSpend, children: [] as { text: string; anchor: string }[] },
  { text: "By workflow", anchor: SECTION.fleetWorkflows, children: [] },
  { text: "Reliability", anchor: SECTION.fleetReliability, children: [] },
  { text: "Landing telemetry", anchor: SECTION.fleetLanding, children: [] },
  { text: "Recent runs", anchor: SECTION.fleetRuns, children: [] },
  { text: "Your coding agents", anchor: SECTION.youUsage, children: [] },
];

const RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

const rangeSearchSchema = routeSearchSchema({
  range: z.string().optional(),
});

function pct(x: number | null | undefined): string {
  return x == null ? "—" : `${Math.round(x * 100)}%`;
}
function fmtDur(ms: number): string {
  if (!ms) return "—";
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  return `${Math.round(ms / 1000)}s`;
}
function ratio(x: number | null | undefined): string {
  return x == null ? "—" : Number(x).toFixed(2);
}
function shortModel(m: string): string {
  if (m === "unattributed") return "unattributed";
  if (m.includes(",")) return "mixed";
  const oneM = /\[1m\]/.test(m);
  const cleaned = m.replace(/\[1m\]/g, "").replace(/^claude-/, "");
  const mm = cleaned.match(/^(opus|fable|haiku|sonnet)-(\d+)-(\d+)/i);
  const label = mm
    ? `${mm[1]![0]!.toUpperCase()}${mm[1]!.slice(1)} ${mm[2]}.${mm[3]}`
    : cleaned;
  return oneM ? `${label} · 1M` : label;
}
function shortTool(t: string): string {
  if (t.startsWith("mcp__excellent__"))
    return `excellent · ${t.slice("mcp__excellent__".length)}`;
  if (t.startsWith("mcp__")) return t.slice(5).replace(/__/g, " · ");
  return t;
}

function SubSection({
  id,
  title,
  action,
  children,
}: {
  id: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div id={id} className="scroll-mt-24 flex items-center justify-between gap-4">
        <h3 className="type-title font-medium text-foreground">{title}</h3>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function DateFilter({ range, className }: { range: string; className?: string }) {
  const nav = useNavigate();
  return (
    <Select
      value={range}
      onValueChange={(v) => nav.push(`/agents/analytics?range=${v}`)}
    >
      <SelectTrigger className={className ?? "w-full"}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {RANGES.map((r) => (
          <SelectItem key={r.value} value={r.value}>
            {r.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AnalyticsRail({ range }: { range: string }) {
  const TOC = ANALYTICS_OUTLINE.map((g) => ({
    text: g.text,
    anchor: g.anchor,
    children: g.children.map((c) => ({ text: c.text, anchor: c.anchor })),
  }));
  const extension = useMemo<RailExtension>(
    () => ({
      side: "right",
      groupLabel: "Analytics",
      activeHref: "",
      links: [],
      toc: TOC,
      placement: "reserve",
      controls: (
        <div className="flex flex-col gap-2">
          <span className="px-1 type-subtext font-medium uppercase tracking-[0.08em] text-muted-foreground/70">
            Time period
          </span>
          <DateFilter range={range} className="w-full" />
        </div>
      ),
    }),
    // TOC is module-constant; only range drives re-push.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range],
  );
  useProvideRailExtension(extension);
  return null;
}

function WorkerRunsFeedPaged() {
  const nav = useNavigate();
  const { rows, hasMore, loadMore, isLoading, isLoadingMore } = useCursorList(
    agentRuns.feed,
    { limit: 30 },
  );
  return (
    <WorkerRunsTable
      runs={rows as WorkerRunItem[]}
      basePath="/agents/goals"
      onRowClick={(r) => nav.push(`/agents/runs/${r.id}`)}
      title="Recent runs"
      empty={isLoading ? "Loading…" : "No worker runs recorded yet."}
      footer={
        hasMore ? (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={loadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? "Loading…" : "Load more runs"}
            </Button>
          </div>
        ) : undefined
      }
    />
  );
}

// ── hub page ────────────────────────────────────────────────────────────────

type Row = Record<string, unknown>;

function AnalyticsHubPage() {
  // TanStack search is typed via validateSearch on the route.
  const search = agentsAnalyticsRoute.useSearch() as { range?: string };
  const range = search.range === "7d" || search.range === "90d" || search.range === "all"
    ? search.range
    : "30d";

  const { data, error, isLoading } = useApi(agentAnalytics.page, {
    query: { range },
  });

  if (isLoading && !data) {
    return (
      <div className="px-6 py-8 space-y-4">
        <PageHeader title="Analytics" description="Loading fleet ledger…" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="px-6 py-8 space-y-4">
        <PageHeader
          title="Analytics"
          description={error?.message ?? "Could not load analytics."}
        />
      </div>
    );
  }

  const summary = data.summary as {
    cost: number;
    runs: number;
    completed: number;
    running: number;
    landed: number;
    out_tok: number;
    wall_ms: number;
    successRate: number | null;
    costPerLanded: number | null;
    cacheRate: number | null;
    delta: { landed?: number | null; cost?: number | null };
  };
  const roles = data.daily.roles;
  const points = data.daily.points as Array<Record<string, number | string>>;
  const roleRows = data.roleRows as Array<Row & { role: string; runs: number; completed: number; shipped: number; verified: number; cost: number; avg_wall_ms: number }>;
  const modelRows = data.modelRows as Array<Row & { model: string; runs: number; tokens: number; cost: number; cost_rows: number; token_rows: number }>;
  const codingAgentRows = data.codingAgentRows as Array<Row & { coding_agent: string; runs: number; tokens: number; cost: number; cost_rows: number; token_rows: number }>;
  const tools = data.tools;
  const goals = data.goals as Array<Row & { id: number; title: string; runs: number; shipped: number; verified: number; cost: number }>;
  const workflows = data.workflows as Array<Row & { slug: string; title: string; runs: number; tokens: number; cost: number }>;
  const rel = data.reliability as {
    failedRuns: number;
    wastedCost: number;
    retryableShare: number | null;
    failRate: number | null;
    buckets: Array<{ bucket: string; label: string; hint: string; runs: number; cost: number }>;
  };
  const roleEff = data.roleEff as Array<Row & { role: string; shipped: number; runs: number; failed: number; attemptsPerShip: number | null; outPerShip: number | null; costPerShip: number | null; avgWallMs: number }>;
  const landing = data.landing as {
    attempts: number;
    clean: number;
    conflicts: number;
    cleanRate: number | null;
    rows: Array<{ mechanism: string; outcome: string; count: number; avgWallMs: number; files: number; contentMerges: number }>;
    repair: { invocations: number; landsWithRepair: number };
  };
  const rangeLabel = data.rangeLabel;
  const lo = rangeLabel.toLowerCase();
  const totalCost = summary.cost || 1;
  const maxTool = tools.length ? tools[0]!.calls : 1;
  const maxBucket = rel.buckets.length ? rel.buckets[0]!.runs : 1;
  const finished = summary.runs - summary.running;
  const avgRunMs = summary.runs > 0 ? summary.wall_ms / summary.runs : 0;
  const trendLabel = range === "all" ? undefined : `vs prev ${rangeLabel}`;
  const sparkCost = points.map((p) => Number(p.cost) || 0);
  const roleColor = new Map(roles.map((role, i) => [role, chartColor(i)]));
  const series = roles.map((role, i) => ({
    key: `cost_${role}`,
    label: roleLabel(role),
    color: chartColor(i),
  }));
  const chartPoints: FleetSpendPoint[] = points.map((p) => {
    const out: FleetSpendPoint = {
      date: String(p.date),
      runs: Number(p.runs) || 0,
    };
    for (const s of series) out[s.key] = (p[s.key] as number) ?? 0;
    return out;
  });
  const trendPoints = data.trend.map((t) => ({
    date: t.date,
    value: t.tokPerLanded,
  }));

  const roleDot = (role: string) => (
    <span className="flex items-center gap-2">
      <span
        className="size-2 shrink-0 rounded-xs"
        style={{ backgroundColor: roleColor.get(role) ?? chartColor(0) }}
        aria-hidden
      />
      <span className="text-foreground">{roleLabel(role)}</span>
    </span>
  );

  const roleCols: DataTableColumn<(typeof roleRows)[number]>[] = [
    { key: "role", header: "Role", render: (r) => roleDot(r.role) },
    { key: "runs", header: "Runs", render: (r) => <span className={NUM}>{r.runs}</span> },
    {
      key: "success",
      header: "Success",
      render: (r) => (
        <span className={NUM}>{pct(r.runs ? r.completed / r.runs : null)}</span>
      ),
    },
    {
      key: "actions",
      header: "Ships · verifies",
      render: (r) => (
        <span className={MUTED}>
          <span className="text-foreground">{r.shipped}</span>
          {" · "}
          <span className="text-foreground">{r.verified}</span>
        </span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      render: (r) => (
        <span className={NUM}>
          {formatUsd(r.cost)}
          <span className="ml-1 type-subtext text-muted-foreground">
            {Math.round((r.cost / totalCost) * 100)}%
          </span>
        </span>
      ),
    },
    {
      key: "avg",
      header: "Avg time",
      render: (r) => <span className={MUTED}>{fmtDur(r.avg_wall_ms)}</span>,
    },
  ];

  const modelCols: DataTableColumn<(typeof modelRows)[number]>[] = [
    {
      key: "model",
      header: "Model",
      render: (m) => <span className="text-foreground">{shortModel(m.model)}</span>,
    },
    { key: "runs", header: "Runs", render: (m) => <span className={NUM}>{m.runs}</span> },
    {
      key: "tokens",
      header: "Tokens",
      render: (m) => (
        <span className={MUTED}>{m.token_rows === 0 ? "—" : formatCompact(m.tokens)}</span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      render: (m) =>
        m.cost_rows === 0 ? (
          <span className={MUTED}>—</span>
        ) : (
          <span className={NUM}>
            {formatUsd(m.cost)}
            <span className="ml-1 type-subtext text-muted-foreground">
              {Math.round((m.cost / totalCost) * 100)}%
            </span>
          </span>
        ),
    },
  ];

  // Grok/codex uncaptured cost groups must read as "—" (unknown), never "$0.00".
  const codingAgentCols: DataTableColumn<(typeof codingAgentRows)[number]>[] = [
    {
      key: "coding_agent",
      header: "Coding agent",
      render: (r) => (
        <span className="text-foreground capitalize">
          {r.coding_agent === "unattributed" ? "Unattributed" : r.coding_agent}
        </span>
      ),
    },
    { key: "runs", header: "Runs", render: (r) => <span className={NUM}>{r.runs}</span> },
    {
      key: "tokens",
      header: "Tokens",
      render: (r) => (
        <span className={MUTED}>{r.token_rows === 0 ? "—" : formatCompact(r.tokens)}</span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      render: (r) =>
        r.cost_rows === 0 ? (
          <span className={MUTED}>—</span>
        ) : (
          <span className={NUM}>
            {formatUsd(r.cost)}
            <span className="ml-1 type-subtext text-muted-foreground">
              {totalCost > 0 ? `${Math.round((r.cost / totalCost) * 100)}%` : "—"}
            </span>
          </span>
        ),
    },
  ];

  const goalCols: DataTableColumn<(typeof goals)[number]>[] = [
    {
      key: "title",
      header: "Goal",
      render: (g) => (
        <Link
          href={`/agents/goals/${g.id}`}
          className="text-foreground underline-offset-2 hover:underline"
        >
          {g.title}
        </Link>
      ),
    },
    { key: "runs", header: "Runs", render: (g) => <span className={NUM}>{g.runs}</span> },
    {
      key: "actions",
      header: "Ships · verifies",
      render: (g) => (
        <span className={MUTED}>
          <span className="text-foreground">{g.shipped}</span>
          {" · "}
          <span className="text-foreground">{g.verified}</span>
        </span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      render: (g) => <span className={NUM}>{formatUsd(g.cost)}</span>,
    },
  ];

  const workflowCols: DataTableColumn<(typeof workflows)[number]>[] = [
    {
      key: "title",
      header: "Workflow",
      render: (w) => (
        <Link
          href={`/agents/workflows/${w.slug}`}
          className="text-foreground underline-offset-2 hover:underline"
        >
          {w.title}
        </Link>
      ),
    },
    { key: "runs", header: "Runs", render: (w) => <span className={NUM}>{w.runs}</span> },
    {
      key: "tokens",
      header: "Tokens",
      render: (w) => <span className={MUTED}>{formatCompact(w.tokens)}</span>,
    },
    {
      key: "cost",
      header: "Cost",
      render: (w) => (
        <span className={NUM}>
          {formatUsd(w.cost)}
          <span className="ml-1 type-subtext text-muted-foreground">
            {Math.round((w.cost / totalCost) * 100)}%
          </span>
        </span>
      ),
    },
  ];

  const failCols: DataTableColumn<(typeof rel.buckets)[number]>[] = [
    {
      key: "cause",
      header: "Cause",
      render: (b) => (
        <div className="flex flex-col">
          <span className="text-foreground">{b.label}</span>
          <span className="type-subtext text-muted-foreground">{b.hint}</span>
        </div>
      ),
    },
    {
      key: "share",
      header: "Share of failures",
      width: "w-48",
      render: (b) => (
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-lg bg-muted">
            <div
              className="h-full rounded-lg"
              style={{
                width: `${Math.max(3, (b.runs / maxBucket) * 100)}%`,
                backgroundColor:
                  b.bucket === "transient-api" ? chartColor(3) : chartColor(0),
              }}
            />
          </div>
          <span className={`${MUTED} w-12 text-right type-subtext`}>
            {rel.failedRuns ? Math.round((b.runs / rel.failedRuns) * 100) : 0}%
          </span>
        </div>
      ),
    },
    { key: "runs", header: "Runs", render: (b) => <span className={NUM}>{b.runs}</span> },
    {
      key: "wasted",
      header: "Spend burned",
      render: (b) => <span className={MUTED}>{formatUsd(b.cost)}</span>,
    },
  ];

  const effCols: DataTableColumn<(typeof roleEff)[number]>[] = [
    { key: "role", header: "Role", render: (r) => roleDot(r.role) },
    { key: "shipped", header: "Shipped", render: (r) => <span className={NUM}>{r.shipped}</span> },
    {
      key: "fail",
      header: "Fail %",
      render: (r) => (
        <span className={NUM}>{pct(r.runs ? r.failed / r.runs : null)}</span>
      ),
    },
    {
      key: "attempts",
      header: "Attempts / ship",
      render: (r) => <span className={NUM}>{ratio(r.attemptsPerShip)}</span>,
    },
    {
      key: "tokens",
      header: "Out tok / ship",
      render: (r) => (
        <span className={MUTED}>
          {r.outPerShip == null ? "—" : formatCompact(r.outPerShip)}
        </span>
      ),
    },
    {
      key: "cost",
      header: "$ / ship",
      render: (r) => (
        <span className={NUM}>
          {r.costPerShip == null ? "—" : formatUsd(r.costPerShip)}
        </span>
      ),
    },
    {
      key: "avg",
      header: "Avg time",
      render: (r) => <span className={MUTED}>{fmtDur(r.avgWallMs)}</span>,
    },
  ];

  const landCols: DataTableColumn<(typeof landing.rows)[number]>[] = [
    {
      key: "mechanism",
      header: "Mechanism",
      render: (l) => <span className="text-foreground">{l.mechanism}</span>,
    },
    {
      key: "outcome",
      header: "Outcome",
      render: (l) => <span className="text-foreground">{l.outcome}</span>,
    },
    { key: "count", header: "Lands", render: (l) => <span className={NUM}>{l.count}</span> },
    {
      key: "avg",
      header: "Avg time",
      render: (l) => <span className={MUTED}>{fmtDur(l.avgWallMs)}</span>,
    },
    {
      key: "files",
      header: "Files",
      render: (l) => <span className={MUTED}>{formatCompact(l.files)}</span>,
    },
    {
      key: "cm",
      header: "Content merges",
      render: (l) => <span className={MUTED}>{formatCompact(l.contentMerges)}</span>,
    },
  ];

  const fleetHeadline: StatStripItem[] = [
    {
      title: "Verified ships",
      value: formatCompact(summary.landed),
      description: "gate-verified done tasks",
      trend: summary.delta?.landed ?? undefined,
      trendGood: "up",
      trendLabel,
      accentColor: chartColor(1),
    },
    {
      title: "Cost / ship",
      value: summary.costPerLanded == null ? "—" : formatUsd(summary.costPerLanded),
      description: "spend ÷ verified ships",
      accentColor: chartColor(0),
    },
    {
      title: "Fleet spend",
      value: formatUsd(summary.cost),
      description: `${formatCompact(summary.out_tok)} output tokens`,
      trend: summary.delta?.cost ?? undefined,
      trendGood: "down",
      trendLabel,
      sparkData: sparkCost,
      accentColor: chartColor(0),
    },
    {
      title: "Goal-tagged",
      value: pct(data.goalShare),
      description: "of spend on a goal",
      accentColor: chartColor(2),
    },
  ];

  const relHeadline: StatStripItem[] = [
    {
      title: "Run success",
      value: pct(summary.successRate),
      description: `${summary.completed} of ${finished} completed`,
      accentColor: chartColor(1),
    },
    {
      title: "Failed runs",
      value: formatCompact(rel.failedRuns),
      description: `${formatUsd(rel.wastedCost)} burned`,
      accentColor: chartColor(3),
    },
    {
      title: "Retryable",
      value: pct(rel.retryableShare),
      description: "transient-API failures",
      accentColor: chartColor(2),
    },
    {
      title: "Avg run",
      value: fmtDur(avgRunMs),
      description: `${summary.runs} runs`,
      accentColor: chartColor(0),
    },
  ];

  const landingHeadline: StatStripItem[] = [
    {
      title: "Land attempts",
      value: formatCompact(landing.attempts),
      description: `integrateTask lands · last ${lo}`,
      accentColor: chartColor(0),
    },
    {
      title: "Clean rate",
      value: pct(landing.cleanRate),
      description: `${landing.clean} landed clean`,
      accentColor: chartColor(1),
    },
    {
      title: "Conflicts",
      value: formatCompact(landing.conflicts),
      description: "bounced (ladder + parse)",
      accentColor: chartColor(3),
    },
    {
      title: "Repair invocations",
      value: formatCompact(landing.repair.invocations),
      description: `${landing.repair.landsWithRepair} lands w/ auto-repair`,
      accentColor: chartColor(2),
    },
  ];

  const lensStats: StatStripItem[] = data.lensStats.map((s) => ({
    title: s.title,
    value: s.value,
    description: s.description,
    sparkData: s.sparkData,
    accentColor: s.accentColor,
  }));

  return (
    <main className="@container/page flex min-w-0 flex-col gap-12 px-6 py-8">
      <AnalyticsRail range={range} />
      <div className="flex justify-end lg:hidden">
        <DateFilter range={range} className="w-44" />
      </div>
      <div className="flex flex-col gap-10">
        <SubSection id={SECTION.fleetSpend} title="Spend & ROI">
          <StatStrip items={fleetHeadline} variant="standalone" />
          <ListCard
            title="Spend over time"
            subtitle={`${formatUsd(summary.cost)} · ${summary.runs} runs · last ${lo}`}
          >
            <div className="px-6 py-4">
              <FleetSpendChart points={chartPoints} series={series} />
            </div>
          </ListCard>
          <DataTable
            title="By role"
            subtitle="colors match the chart"
            rows={roleRows}
            columns={roleCols}
            rowKey={(r) => r.role}
            empty="No runs in this range."
          />
          <div className="grid grid-cols-1 gap-5 @4xl/page:grid-cols-2">
            <DataTable
              title="By coding agent"
              subtitle="ACP agent (Claude · Grok · Codex · …), not process host"
              rows={codingAgentRows}
              columns={codingAgentCols}
              rowKey={(r) => r.coding_agent}
              empty="No runs in this range."
            />
            <DataTable
              title="Model economics"
              subtitle={`inner model id · cache hit ${pct(summary.cacheRate)}`}
              rows={modelRows}
              columns={modelCols}
              rowKey={(m) => m.model}
              empty="No runs in this range."
            />
            <ListCard
              title="Where effort goes"
              subtitle={
                data.trace.total > 0
                  ? `${Math.round((data.trace.withTrace / data.trace.total) * 100)}% of runs traced`
                  : "top tool calls"
              }
            >
              <div className="flex flex-col gap-2.5 px-6 py-4">
                {tools.length === 0 ? (
                  <p className="py-2 type-body text-muted-foreground">
                    No tool activity in this range.
                  </p>
                ) : (
                  tools.map((t) => (
                    <MiniBarRow
                      key={t.tool}
                      label={shortTool(t.tool)}
                      value={formatCompact(t.calls)}
                      width={(t.calls / maxTool) * 100}
                      color={chartColor(0)}
                      labelWidth="w-44"
                      valueWidth="w-12"
                    />
                  ))
                )}
              </div>
            </ListCard>
          </div>
          <DataTable
            title="Goal ROI"
            subtitle={
              data.goalShare != null ? `${pct(data.goalShare)} goal-tagged` : undefined
            }
            headerAction={<SectionTitleLink href="/agents/goals">All goals</SectionTitleLink>}
            rows={goals}
            columns={goalCols}
            rowKey={(g) => String(g.id)}
            empty="No goal-attributed runs in this range."
          />
        </SubSection>

        <SubSection id={SECTION.fleetWorkflows} title="By workflow">
          <DataTable
            title="Workflow spend"
            subtitle={
              data.workflowShare != null
                ? `${pct(data.workflowShare)} of spend on a workflow`
                : "token & cost per workflow"
            }
            headerAction={
              <SectionTitleLink href="/agents/workflows">All workflows</SectionTitleLink>
            }
            rows={workflows}
            columns={workflowCols}
            rowKey={(w) => w.slug}
            empty="No workflow-attributed runs in this range."
          />
        </SubSection>

        <SubSection id={SECTION.fleetReliability} title="Reliability">
          <StatStrip items={relHeadline} variant="standalone" />
          <DataTable
            title="Why runs fail"
            subtitle={`${pct(rel.failRate)} fail · ${pct(rel.retryableShare)} retryable · ${formatUsd(rel.wastedCost)} burned`}
            rows={rel.buckets}
            columns={failCols}
            rowKey={(b) => b.bucket}
            empty="No failed runs in this range. The fleet ran clean."
          />
          <DataTable
            title="Efficiency by role"
            subtitle="attempts/ship = rework"
            rows={roleEff}
            columns={effCols}
            rowKey={(r) => r.role}
            empty="No runs in this range."
          />
          <ListCard title="Output tokens per landed task" subtitle="lower = leaner">
            <div className="px-6 py-4">
              <FleetTrendChart
                points={trendPoints}
                color={chartColor(0)}
                unit="count"
                valueLabel="tokens / task"
              />
            </div>
          </ListCard>
        </SubSection>

        <SubSection id={SECTION.fleetLanding} title="Landing telemetry">
          <StatStrip items={landingHeadline} variant="standalone" />
          <DataTable
            title="Lands by mechanism & outcome"
            subtitle="one row per integrateTask attempt"
            rows={landing.rows}
            columns={landCols}
            rowKey={(l) => `${l.mechanism}:${l.outcome}`}
            empty="No land attempts in this range yet. Rows appear as the manager integrates verified tasks."
          />
        </SubSection>

        <SubSection id={SECTION.fleetRuns} title="Recent runs">
          <WorkerRunsFeedPaged />
        </SubSection>

        <SubSection
          id={SECTION.youUsage}
          title="Your coding agents"
          action={
            <SectionTitleLink href="/agents/analytics/you">Full dashboard</SectionTitleLink>
          }
        >
          {lensStats.length > 0 ? (
            <StatStrip items={lensStats} variant="standalone" />
          ) : null}
          {data.codingAgentPersonal.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 @3xl/page:grid-cols-2">
              {data.codingAgentPersonal.map((t) => (
                <ListCard
                  key={t.id}
                  title={t.label}
                  subtitle={
                    t.coverage === "full"
                      ? "full session telemetry"
                      : t.coverage === "partial"
                        ? "partial (tokens, no cost)"
                        : "presence only"
                  }
                >
                  <div className="flex flex-col gap-1 px-6 py-4 type-body">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sessions</span>
                      <span className="tabular-nums text-foreground">
                        {t.sessions ?? "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tokens</span>
                      <span className="tabular-nums text-foreground">
                        {t.tokens != null ? formatCompact(t.tokens) : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cost</span>
                      <span className="tabular-nums text-foreground">
                        {t.costUsd != null ? formatUsd(t.costUsd) : "—"}
                      </span>
                    </div>
                    {t.lastActive ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last active</span>
                        <span className="text-foreground">{t.lastActive}</span>
                      </div>
                    ) : null}
                  </div>
                </ListCard>
              ))}
            </div>
          ) : lensStats.length === 0 ? (
            <ListCard title="Your coding agents" subtitle="local session telemetry">
              <p className="px-6 py-6 type-body text-muted-foreground">
                No coding-agent session data yet. Activity from{" "}
                <code className="text-foreground">~/.claude</code>,{" "}
                <code className="text-foreground">~/.codex</code>,{" "}
                <code className="text-foreground">~/.gemini</code>, and{" "}
                <code className="text-foreground">~/.grok</code> appears here as you
                use those tools.
              </p>
            </ListCard>
          ) : null}
        </SubSection>
      </div>
    </main>
  );
}

// ── You chrome ──────────────────────────────────────────────────────────────

const ICON = "size-4";

const YOU_NAV: SectionNavGroup[] = [
  {
    items: [
      {
        href: "/agents/analytics",
        label: "← Analytics",
        icon: <ArrowLeft className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you",
        label: "Summary",
        icon: <Compass className={ICON} strokeWidth={1.75} />,
      },
    ],
  },
  {
    label: "Deep dives",
    caption: "drill into one dimension",
    items: [
      {
        href: "/agents/analytics/you/sessions",
        label: "Sessions",
        icon: <MessageSquare className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/costs",
        label: "Costs",
        icon: <CircleDollarSign className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/activity",
        label: "Activity",
        icon: <Activity className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/tools",
        label: "Tools",
        icon: <Wrench className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/projects",
        label: "Projects",
        icon: <FolderKanban className={ICON} strokeWidth={1.75} />,
      },
    ],
  },
  {
    label: "Library",
    caption: "local coding-agent artifacts",
    items: [
      {
        href: "/agents/analytics/you/memory",
        label: "Memory",
        icon: <Brain className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/plans",
        label: "Plans",
        icon: <ClipboardList className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/todos",
        label: "Todos",
        icon: <ListTodo className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/history",
        label: "History",
        icon: <History className={ICON} strokeWidth={1.75} />,
      },
    ],
  },
  {
    label: "Manage",
    items: [
      {
        href: "/agents/analytics/you/export",
        label: "Export",
        icon: <Download className={ICON} strokeWidth={1.75} />,
      },
      {
        href: "/agents/analytics/you/settings",
        label: "Settings",
        icon: <SettingsIcon className={ICON} strokeWidth={1.75} />,
      },
    ],
  },
];

const MOBILE_TABS: NavTab[] = YOU_NAV.flatMap((g) =>
  g.items.map((it) => ({ href: it.href, label: it.label, icon: it.icon })),
);

function YouChrome({ children }: { children: ReactNode }) {
  return (
    <div className="flex">
      <div className="min-w-0 flex-1">
        <div className="border-b border-border/60 px-6 pt-2 lg:hidden">
          <NavTabs
            className="gap-5 overflow-x-auto no-scrollbar"
            tabs={MOBILE_TABS}
          />
        </div>
        {children}
      </div>
      <aside className="hidden w-56 shrink-0 border-l border-border/60 lg:block">
        <div className="sticky top-0 px-2 py-8">
          <SectionNavRail groups={YOU_NAV} ariaLabel="Your coding agent sections" />
        </div>
      </aside>
    </div>
  );
}

const remapLensHref = makeSectionHrefRemapper({
  prefix: "lens",
  root: "analytics/you",
  subPath: "analytics/you",
});

function AnalyticsYouHome() {
  const search = agentsAnalyticsYouRoute.useSearch() as { range?: string };
  const { data, error, isLoading } = useApi(agentAnalytics.you, {
    query: search.range ? { range: search.range as "7d" | "30d" | "90d" | "all" } : {},
  });

  if (isLoading && !data) {
    return (
      <YouChrome>
        <div className="px-6 py-8">
          <PageHeader title="You" description="Loading personal coding-agent usage…" />
        </div>
      </YouChrome>
    );
  }
  if (error || !data) {
    return (
      <YouChrome>
        <div className="px-6 py-8">
          <PageHeader
            title="You"
            description={error?.message ?? "Could not load personal usage."}
          />
        </div>
      </YouChrome>
    );
  }

  return (
    <YouChrome>
      <SectionProductDashboard
        productName="lens"
        label={data.label}
        basePath="/agents"
        rangeBasePath="/agents/analytics/you"
        summary={data.summary as never}
        tagline={data.tagline}
        timeScoped={data.timeScoped}
        currentRange={data.currentRange}
        activityEmptyBody="No activity yet. Coding-agent sessions (Claude, and other brands on the Analytics hub) will appear here as you use them."
        remapHref={remapLensHref}
      />
    </YouChrome>
  );
}

// ── You leaf pages (lens client re-homes) ───────────────────────────────────

function wrapYou(Page: () => ReactNode) {
  return function YouLeaf() {
    return (
      <YouChrome>
        <div className="px-6 py-6">
          <Page />
        </div>
      </YouChrome>
    );
  };
}

function YouProjectsIndex() {
  return (
    <YouChrome>
      <div className="px-6 py-6">
        <ProjectsClient />
      </div>
    </YouChrome>
  );
}

function YouIntegrations() {
  return (
    <YouChrome>
      <div className="px-6 py-6">
        <SettingsPageShell
          title="Integrations"
          description="Sync repositories and commits from GitHub. Connecting and per-connection settings live in the Integrations hub."
        >
          <ProductIntegrationsPanel productLabel="Analytics" providers={[]} />
        </SettingsPageShell>
      </div>
    </YouChrome>
  );
}

function YouSessionDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  return (
    <YouChrome>
      <div className="px-6 py-6">
        <SessionDetailPage params={Promise.resolve({ id })} />
      </div>
    </YouChrome>
  );
}

function YouProjectDetail() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { data, error, isLoading } = useApi(
    lens.projectBySlug,
    slug ? { params: { slug } } : null,
  );
  return (
    <YouChrome>
      <div className="space-y-4 px-6 py-6">
        {isLoading && !data ? (
          <p className="type-body text-muted-foreground">Loading project…</p>
        ) : error || !data ? (
          <p className="type-body text-destructive">
            {error instanceof Error ? error.message : "Could not load project."}
          </p>
        ) : (
          <>
            <PageHeader title={data.display_name} description={data.project_path} />
            <p className="type-body text-muted-foreground">
              {data.sessions?.length ?? 0} sessions
            </p>
            <ul className="space-y-1 type-body">
              {(data.sessions ?? []).slice(0, 40).map((s) => (
                <li key={s.session_id}>
                  <Link
                    href={`/agents/analytics/you/sessions/${s.session_id}`}
                    className="text-foreground underline-offset-2 hover:underline"
                  >
                    {s.session_id.slice(0, 12)}…
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </YouChrome>
  );
}

// ── routes ──────────────────────────────────────────────────────────────────

export const agentsAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics",
  validateSearch: rangeSearchSchema,
  component: AnalyticsHubPage,
});

export const agentsAnalyticsYouRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you",
  validateSearch: rangeSearchSchema,
  component: AnalyticsYouHome,
});

export const agentsAnalyticsYouActivityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/activity",
  component: wrapYou(ActivityPage),
});

export const agentsAnalyticsYouCostsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/costs",
  component: wrapYou(CostsPage),
});

export const agentsAnalyticsYouExportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/export",
  component: wrapYou(ExportPage),
});

export const agentsAnalyticsYouHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/history",
  component: wrapYou(HistoryPage),
});

export const agentsAnalyticsYouMemoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/memory",
  component: wrapYou(MemoryPage),
});

export const agentsAnalyticsYouPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/plans",
  component: wrapYou(PlansPage),
});

export const agentsAnalyticsYouProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/projects",
  component: YouProjectsIndex,
});

export const agentsAnalyticsYouSessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/sessions",
  component: wrapYou(SessionsPage),
});

export const agentsAnalyticsYouSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/settings",
  component: wrapYou(SettingsPage),
});

export const agentsAnalyticsYouTodosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/todos",
  component: wrapYou(TodosPage),
});

export const agentsAnalyticsYouToolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/tools",
  component: wrapYou(ToolsPage),
});

export const agentsAnalyticsYouIntegrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/settings/integrations",
  component: YouIntegrations,
});

export const agentsAnalyticsYouProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/projects/$slug",
  component: YouProjectDetail,
});

export const agentsAnalyticsYouSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/you/sessions/$id",
  component: YouSessionDetail,
});
